import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs/operators';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@Injectable()
export class GlobalErrorHandlerService extends PuSubscribable implements ErrorHandler {
  private sentryHandler = Sentry.createErrorHandler({ showDialog: false });

  private lastAttemptedUrl: string | null = null;
  private readonly maxRetries = 5;
  private retryCount = 0;

  constructor(private router: Router, private zone: NgZone, private snackbarService: SnackbarService) {
    super();
    this.router.events
      .pipe(filter(e => e instanceof NavigationError))
      .subscribe((event: NavigationError) => {
        if (event?.error && this.isChunkError(event.error)) {
          this.lastAttemptedUrl = event.url;
        }
      })
      .untilDestroyed(this);
  }

  handleError(error: any): void {
    this.sentryHandler.handleError(error);

    if (this.isChunkError(error)) {
      this.handleChunkLoadError(error);
    }
  }

  private handleChunkLoadError(error: any) {
    console.warn('Chunk load failed, attempting to reload chunk...', error);

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.retryChunkLoad(error);
    } else {
      this.reloadApp();
    }
  }

  private retryChunkLoad(error: any) {
    const chunkUrl = this.extractChunkUrl(error);
    if (!chunkUrl) {
      console.error('Could not extract chunk URL');
      return;
    }

    console.warn(`Retrying chunk load (attempt ${this.retryCount}/${this.maxRetries}): ${chunkUrl}`);

    const newScript = document.createElement('script');
    newScript.src = this.addCacheBustParam(chunkUrl);

    newScript.onload = () => {
      this.retryCount = 0;

      setTimeout(() => {
        // eslint-disable-next-line angular/module-getter
        this.zone.run(() => {
          const config = this.router.config;
          this.router.resetConfig(config);
          this.router.navigateByUrl(this.lastAttemptedUrl || this.router.url, { replaceUrl: true });
          this.lastAttemptedUrl = null;
        });
      }, 500);
    };

    newScript.onerror = loadError => {
      console.error('Chunk reload failed:', loadError);
      setTimeout(() => this.handleChunkLoadError(error), 1000);
    };

    document.head.appendChild(newScript);
  }

  private extractChunkUrl(error: any): string | null {
    if (!error) return null;

    const message = error.message || error.toString();
    const urlMatch = message.match(/(https?:\/\/[^\s]+\.js)/);
    return urlMatch ? urlMatch[1] : null;
  }

  private addCacheBustParam(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_t=${Date.now()}`;
  }

  private reloadApp() {
    this.snackbarService.warning('Loading error occurred. Restarting the application to recover...');
    console.error('Max retries exceeded, reloading page');
    setTimeout(() => window.location.reload(), 2000);
  }

  private isChunkError(error: any): boolean {
    if (!error) return false;
    const errorMessage = error.message || error.error?.toString() || error.toString() || '';
    return /Loading chunk [\w-]+ failed/.test(errorMessage) || /ChunkLoadError/.test(errorMessage);
  }
}
